import { authService } from '../supabaseAuthService'
import { supabase } from '../supabase'
import type {
  ProgressUpdateData,
  UserProgress,
  AuthError,
} from '../types/auth'

/**
 * Progress Management API
 * 
 * Handles user learning progress, achievements, and analytics
 */

export class ProgressAPI {
  private static instance: ProgressAPI

  private constructor() {}

  public static getInstance(): ProgressAPI {
    if (!ProgressAPI.instance) {
      ProgressAPI.instance = new ProgressAPI()
    }
    return ProgressAPI.instance
  }

  /**
   * Update user progress for a module/concept
   * POST /api/progress/update
   */
  async updateProgress(userId: string, progress: ProgressUpdateData): Promise<{ success: boolean; error: AuthError | null }> {
    try {
      const result = await authService.updateProgress(userId, progress)
      
      // Trigger achievement check if module completed
      if (progress.progressPercentage === 100) {
        await this.checkAchievements(userId, progress.moduleId)
      }

      return {
        success: !result.error,
        error: result.error,
      }
    } catch (error) {
      return {
        success: false,
        error: { message: error.message || 'Failed to update progress' },
      }
    }
  }

  /**
   * Get user's progress across all modules
   * GET /api/progress/user/:userId
   */
  async getUserProgress(userId: string): Promise<UserProgress[]> {
    try {
      const { data, error } = await supabase
        .from('user_progress')
        .select('*')
        .eq('user_id', userId)
        .order('last_accessed_at', { ascending: false })

      if (error) {
        console.error('Error fetching user progress:', error)
        return []
      }

      return data || []
    } catch (error) {
      console.error('Error fetching user progress:', error)
      return []
    }
  }

  /**
   * Get progress for a specific module
   * GET /api/progress/module/:moduleId/user/:userId
   */
  async getModuleProgress(userId: string, moduleId: string): Promise<UserProgress | null> {
    try {
      const { data, error } = await supabase
        .from('user_progress')
        .select('*')
        .eq('user_id', userId)
        .eq('module_id', moduleId)
        .single()

      if (error) {
        console.error('Error fetching module progress:', error)
        return null
      }

      return data
    } catch (error) {
      console.error('Error fetching module progress:', error)
      return null
    }
  }

  /**
   * Get learning analytics for a user
   * GET /api/progress/analytics/:userId
   */
  async getLearningAnalytics(userId: string): Promise<{
    totalTimeSpent: number
    modulesCompleted: number
    averageScore: number
    streakDays: number
    recentActivity: UserProgress[]
    error: AuthError | null
  }> {
    try {
      const progress = await this.getUserProgress(userId)

      const totalTimeSpent = progress.reduce((sum, p) => sum + p.time_spent_minutes, 0)
      const modulesCompleted = progress.filter(p => p.progress_percentage === 100).length
      const averageScore = progress.length > 0 
        ? progress.reduce((sum, p) => sum + p.progress_percentage, 0) / progress.length 
        : 0

      // Calculate streak (simplified - last 7 days with activity)
      const recentDays = 7
      const now = new Date()
      const recentActivity = progress.filter(p => {
        const lastAccessed = new Date(p.last_accessed_at)
        const daysDiff = Math.floor((now.getTime() - lastAccessed.getTime()) / (1000 * 60 * 60 * 24))
        return daysDiff <= recentDays
      })

      const streakDays = this.calculateStreakDays(progress)

      return {
        totalTimeSpent,
        modulesCompleted,
        averageScore: Math.round(averageScore),
        streakDays,
        recentActivity: recentActivity.slice(0, 10), // Last 10 activities
        error: null,
      }
    } catch (error) {
      return {
        totalTimeSpent: 0,
        modulesCompleted: 0,
        averageScore: 0,
        streakDays: 0,
        recentActivity: [],
        error: { message: error.message || 'Failed to fetch analytics' },
      }
    }
  }

  /**
   * Record time spent on an activity
   * POST /api/progress/time
   */
  async recordTimeSpent(userId: string, moduleId: string, minutes: number): Promise<{ success: boolean; error: AuthError | null }> {
    try {
      // Get current progress
      const currentProgress = await this.getModuleProgress(userId, moduleId)
      
      const newTimeSpent = (currentProgress?.time_spent_minutes || 0) + minutes

      const { error } = await supabase
        .from('user_progress')
        .upsert({
          user_id: userId,
          module_id: moduleId,
          time_spent_minutes: newTimeSpent,
          last_accessed_at: new Date().toISOString(),
          attempts: (currentProgress?.attempts || 0) + (minutes > 0 ? 1 : 0),
        })

      if (error) {
        return {
          success: false,
          error: { message: error.message },
        }
      }

      return {
        success: true,
        error: null,
      }
    } catch (error) {
      return {
        success: false,
        error: { message: error.message || 'Failed to record time' },
      }
    }
  }

  /**
   * Check and award achievements
   * POST /api/progress/achievements/check
   */
  private async checkAchievements(userId: string, moduleId: string): Promise<void> {
    try {
      const analytics = await this.getLearningAnalytics(userId)
      
      // Check for various achievements
      const achievements = []

      // First module completion
      if (analytics.modulesCompleted === 1) {
        achievements.push({
          achievement_type: 'milestone',
          achievement_id: 'first_module',
          title: 'First Steps!',
          description: 'Completed your first learning module',
          icon_url: '/icons/achievements/first-steps.png',
          points: 10,
        })
      }

      // Time-based achievements
      if (analytics.totalTimeSpent >= 60) { // 1 hour
        achievements.push({
          achievement_type: 'time',
          achievement_id: 'hour_learner',
          title: 'Dedicated Learner',
          description: 'Spent 1 hour learning',
          icon_url: '/icons/achievements/time-1h.png',
          points: 5,
        })
      }

      // Streak achievements
      if (analytics.streakDays >= 3) {
        achievements.push({
          achievement_type: 'streak',
          achievement_id: 'streak_3',
          title: 'On Fire!',
          description: '3-day learning streak',
          icon_url: '/icons/achievements/streak-3.png',
          points: 15,
        })
      }

      // Award achievements
      for (const achievement of achievements) {
        await this.awardAchievement(userId, achievement)
      }
    } catch (error) {
      console.error('Error checking achievements:', error)
    }
  }

  /**
   * Award an achievement to a user
   * POST /api/progress/achievements/award
   */
  private async awardAchievement(userId: string, achievement: any): Promise<void> {
    try {
      // Check if already awarded
      const { data: existing } = await supabase
        .from('user_achievements')
        .select('id')
        .eq('user_id', userId)
        .eq('achievement_type', achievement.achievement_type)
        .eq('achievement_id', achievement.achievement_id)
        .single()

      if (existing) {
        return // Already awarded
      }

      // Award the achievement
      const { error } = await supabase
        .from('user_achievements')
        .insert({
          user_id: userId,
          ...achievement,
        })

      if (error) {
        console.error('Error awarding achievement:', error)
      }
    } catch (error) {
      console.error('Error awarding achievement:', error)
    }
  }

  /**
   * Get user's achievements
   * GET /api/progress/achievements/:userId
   */
  async getUserAchievements(userId: string) {
    try {
      const { data, error } = await supabase
        .from('user_achievements')
        .select('*')
        .eq('user_id', userId)
        .order('earned_at', { ascending: false })

      if (error) {
        console.error('Error fetching achievements:', error)
        return []
      }

      return data || []
    } catch (error) {
      console.error('Error fetching achievements:', error)
      return []
    }
  }

  /**
   * Calculate learning streak days
   */
  private calculateStreakDays(progress: UserProgress[]): number {
    if (progress.length === 0) return 0

    const today = new Date()
    today.setHours(0, 0, 0, 0)

    // Group activities by date
    const activeDates = new Set()
    progress.forEach(p => {
      const date = new Date(p.last_accessed_at)
      date.setHours(0, 0, 0, 0)
      activeDates.add(date.getTime())
    })

    const sortedDates = Array.from(activeDates).sort((a, b) => b - a)
    
    let streak = 0
    let currentDate = today.getTime()

    for (const date of sortedDates) {
      if (date === currentDate || date === currentDate - 86400000) { // Same day or previous day
        streak++
        currentDate = date - 86400000 // Move to previous day
      } else {
        break
      }
    }

    return streak
  }
}

// Create and export singleton instance
export const progressAPI = ProgressAPI.getInstance()

// Export individual functions
export const {
  updateProgress,
  getUserProgress,
  getModuleProgress,
  getLearningAnalytics,
  recordTimeSpent,
  getUserAchievements,
} = progressAPI